import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef }
	from '@angular/material/dialog';
import { MatRippleModule } from '@angular/material/core';
import { NgFor, NgIf, NgSwitch, NgSwitchCase, KeyValuePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import { pageTransitions } from '../../animations/page-transitions';
import { UserService } from '../../services/user.service';
import { Widget, WidgetType } from '../../models/widget';

import { AvailableWidget, AvailableWidgetDetails } from './widgets/available-widgets';
import { RtsContentSelectorComponent } from './widgets/rts-content-selector.component';
import { IssueFollowUpContentSelectorComponent } from './widgets/issue-follow-up-content-selector.component';
import { BiggestStocksSelectorComponent } from './widgets/biggest-stocks-selector.component';

function getWidgetDetails(availableWidget: AvailableWidget) {
	switch (availableWidget) {
	case AvailableWidget.RTS:
		return {
			name: 'RtsWidget',
			translationKey: 'dashboard.widget.material-ready-to-ship',
			widgetType: WidgetType.INFORMATION,
			requiredServices: ['PORDT']
		};
	case AvailableWidget.ISSUE_FOLLOW_UP:
		return {
			name: 'IssueFollowUpWidget',
			translationKey: 'issue.qualityfeedback',
			widgetType: WidgetType.INFORMATION,
			requiredServices: []
		};
	case AvailableWidget.BIGGEST_STOCKS:
		return {
			name: 'BiggestStocksWidget',
			translationKey: 'dashboard.widget.highest-stocks',
			widgetType: WidgetType.INFORMATION,
			requiredServices: ['PORDT']
		};
	default:
		return null;
	}
}

export interface DialogData {
	widgetType: WidgetType;
	currentWidget?: Widget;
}

@Component({
	selector: 'copo-widget-add',
	templateUrl: './widget-add.component.html',
	styleUrl: './widget-add.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		NgFor,
		NgIf,
		NgSwitch,
		NgSwitchCase,
		BiggestStocksSelectorComponent,
		IssueFollowUpContentSelectorComponent,
		RtsContentSelectorComponent,
		MatRippleModule,
		KeyValuePipe,
		MatDialogModule,
		MatButtonModule
	]
})
export class WidgetAddComponent {
	allAvailableWidgets = new Map<AvailableWidgetDetails, unknown>();
	selectedWidget?: AvailableWidgetDetails;
	titleWidgetType: string;
	widgetType: WidgetType;
	currentContent?: unknown;
	oldContent?: unknown;

	readonly BIGGEST_STOCKS_WIDGET = 'BiggestStocksWidget';
	readonly ISSUE_FOLLOW_UP_WIDGET = 'IssueFollowUpWidget';
	readonly RTS_WIDGET = 'RtsWidget';

	constructor(
		private readonly userService: UserService,

		private readonly dialogRef: MatDialogRef<WidgetAddComponent>,

		@Inject(MAT_DIALOG_DATA) public data: DialogData
	) {
		const { user } = this.userService;

		this.widgetType = data.widgetType;
		this.titleWidgetType = data.widgetType === WidgetType.INDICATOR
			? 'dashboard.indicator' : 'dashboard.information';

		for (const key of Object.keys(AvailableWidget)) {
			const widget = getWidgetDetails(AvailableWidget[<keyof typeof AvailableWidget> key]);
			if (!(widget && widget.widgetType === data.widgetType)) {
				continue;
			}
			let hasServices = true;
			for (const service of widget.requiredServices) {
				if (!user.hasService(service)) {
					hasServices = false;
					break;
				}
			}
			if (!hasServices) {
				continue;
			}
			if (this.isCurrentWidget(widget)) {
				this.allAvailableWidgets.set(widget, this.getCurrentContent());
				this.onSelectWidget(widget);
			} else {
				this.allAvailableWidgets.set(widget, undefined);
			}
		}
	}

	onSelectWidget(widget: AvailableWidgetDetails) {
		this.selectedWidget = widget;
		this.currentContent = undefined;
		this.oldContent = this.allAvailableWidgets.get(widget);
	}

	onCancelClick() {
		this.dialogRef.close();
	}

	onValidateClick(content: unknown) {
		this.dialogRef.close({
			type: this.widgetType,
			name: this.selectedWidget ? this.selectedWidget.name : '',
			content,
			personId: this.userService.user.id
		});
	}

	private getCurrentContent() {
		if (this.data.currentWidget) {
			return this.data.currentWidget.content;
		}
		return undefined;
	}

	private isCurrentWidget(widget: AvailableWidgetDetails) {
		return Boolean(this.data.currentWidget && this.data.currentWidget.name === widget.name);
	}
}
